import type { IdentityVerificationSummary } from "@repo/shared/types";
import { BadgeCheck, CircleDashed } from "lucide-react";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./verified-id-subjects.i18n";

type VerifiedIdSubjectsProps = {
  summary: IdentityVerificationSummary | null | undefined;
};

/**
 * QL-654 - who has verified their identity and who is missing, when SEVERAL
 * people have to (two or more representatives). The single Verified ID icon only
 * says "everyone is verified"; a reviewer looking at a client where one of two
 * has verified needs to see WHO is missing. Renders nothing for a single person
 * (their one badge already says it all).
 */
export function VerifiedIdSubjects({ summary }: VerifiedIdSubjectsProps) {
  const t = useT(T);
  const subjects = summary?.subjects;
  if (!subjects || subjects.length < 2) return null;
  return (
    <ul
      aria-label={t.verifiedIdPeople}
      className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground"
    >
      {subjects.map((subject) => (
        <li
          key={subject.subjectId ?? subject.name}
          className="inline-flex items-center gap-1"
          title={subject.verified ? t.verifiedId : t.notVerified}
        >
          {subject.verified ? (
            <BadgeCheck className="h-3 w-3 text-success" aria-hidden="true" />
          ) : (
            <CircleDashed className="h-3 w-3" aria-hidden="true" />
          )}
          <span>
            {subject.name ?? "-"}
            <span className="sr-only">: {subject.verified ? t.verifiedId : t.notVerified}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}
